import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "../components/ui/sheet";
import { useStore } from "../lib/store";
import { ProductCard } from "../components/site/ProductCard";
import { Reveal } from "../components/site/Reveal";

const CATS = ["Fashion", "Accessories", "Home & Living", "Gifts"];
const PRICES = [["under-25", "Under $25", 0, 25], ["25-50", "$25 – $50", 25, 50], ["50-100", "$50 – $100", 50, 100], ["over-100", "Over $100", 100, 1e9]];
const SORTS = [["featured", "Featured"], ["price-asc", "Price: Low to High"], ["price-desc", "Price: High to Low"], ["rating", "Top Rated"]];
const PER_PAGE = 9;
const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

const Check = ({ checked, onChange, label, count, testId }) => (
  <label className="flex cursor-pointer items-center gap-3 py-1.5 text-sm text-plum/85 hover:text-magenta">
    <input type="checkbox" checked={checked} onChange={onChange} data-testid={testId} className="h-4 w-4 accent-[#D61F69]" />
    <span className="flex-1">{label}</span>
    {count !== undefined && <span className="text-xs text-plum/55">{count}</span>}
  </label>
);

const Filters = ({ products, params, setParam, toggleList }) => {
  const cats = params.getAll("category");
  const prices = params.getAll("price");
  const colors = params.getAll("color");
  const allColors = [...new Set(products.flatMap((p) => p.colors ?? []))].slice(0, 12);
  return (
    <div className="space-y-9">
      <div>
        <p className="eyebrow text-gold-deep">Category</p>
        <div className="mt-3">
          {CATS.map((c) => (
            <Check key={c} label={c} checked={cats.includes(c)} onChange={() => toggleList("category", c)} count={products.filter((p) => p.category === c).length} testId={`filter-category-${slug(c)}`} />
          ))}
        </div>
      </div>
      <div>
        <p className="eyebrow text-gold-deep">Price</p>
        <div className="mt-3">
          {PRICES.map(([k, l]) => <Check key={k} label={l} checked={prices.includes(k)} onChange={() => toggleList("price", k)} testId={`filter-price-${k}`} />)}
        </div>
      </div>
      <div>
        <p className="eyebrow text-gold-deep">Color</p>
        <div className="mt-4 flex flex-wrap gap-2.5">
          {allColors.map((c) => (
            <button key={c} aria-label={`Color ${c}`} data-testid={`filter-color-${c.replace("#", "")}`} onClick={() => toggleList("color", c)} className={`h-8 w-8 rounded-full border transition-transform hover:scale-110 ${colors.includes(c) ? "ring-2 ring-magenta ring-offset-2 ring-offset-cream" : "border-plum/20"}`} style={{ background: c }} />
          ))}
        </div>
      </div>
      <div>
        <p className="eyebrow text-gold-deep">Show</p>
        <div className="mt-3">
          <Check label="New arrivals" checked={params.get("tag") === "new"} onChange={() => setParam("tag", params.get("tag") === "new" ? null : "new")} testId="filter-tag-new" />
          <Check label="On sale" checked={params.get("tag") === "sale"} onChange={() => setParam("tag", params.get("tag") === "sale" ? null : "sale")} testId="filter-tag-sale" />
        </div>
      </div>
    </div>
  );
};

export default function Shop() {
  const { products, loading } = useStore();
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const page = Math.max(1, Number(params.get("page") ?? 1));
  const sort = params.get("sort") ?? "featured";
  const q = (params.get("q") ?? "").toLowerCase();

  const update = (fn) => {
    const next = new URLSearchParams(params);
    fn(next);
    next.delete("page");
    setParams(next, { preventScrollReset: true });
  };
  const setParam = (k, v) => update((n) => (v ? n.set(k, v) : n.delete(k)));
  const toggleList = (k, v) => update((n) => {
    const cur = n.getAll(k);
    n.delete(k);
    (cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]).forEach((x) => n.append(k, x));
  });

  const filtered = useMemo(() => {
    const cats = params.getAll("category");
    const prices = params.getAll("price");
    const colors = params.getAll("color");
    const tag = params.get("tag");
    let list = products.filter((p) =>
      (!cats.length || cats.includes(p.category)) &&
      (!prices.length || prices.some((k) => { const r = PRICES.find((x) => x[0] === k); return r && p.price >= r[2] && p.price < r[3]; })) &&
      (!colors.length || (p.colors ?? []).some((c) => colors.includes(c))) &&
      (!tag || (tag === "new" ? p.badge === "New" : p.badge === "Sale")) &&
      (!q || `${p.name} ${p.category} ${p.description}`.toLowerCase().includes(q)));
    if (sort === "price-asc") list = [...list].sort((a, b) => a.price - b.price);
    if (sort === "price-desc") list = [...list].sort((a, b) => b.price - a.price);
    if (sort === "rating") list = [...list].sort((a, b) => b.rating - a.rating);
    return list;
  }, [products, params, sort, q]);

  const pages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const shown = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const activeCount = ["category", "price", "color", "tag", "q"].reduce((s, k) => s + params.getAll(k).length, 0);
  const title = params.getAll("category").length === 1 ? params.get("category") : params.get("tag") === "sale" ? "The Sale" : params.get("tag") === "new" ? "New Arrivals" : "Shop All";

  return (
    <main data-testid="shop-page" className="pb-24">
      <section className="relative overflow-hidden bg-plum text-cream grain">
        <img src="/images/cat-accessories.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-20 text-center">
          <Reveal><p className="eyebrow text-gold">Luxe Dream Market</p></Reveal>
          <Reveal delay={0.1}><h1 data-testid="shop-heading" className="mt-3 font-display text-5xl sm:text-6xl lg:text-7xl italic">{title}</h1></Reveal>
          <Reveal delay={0.2}><p className="mt-4 eyebrow text-[10px] text-cream/80">Fashion • Accessories • Home & Living • Gifts</p></Reveal>
        </div>
      </section>

      <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-8 pt-12 lg:grid-cols-[240px_1fr]">
        <aside className="hidden lg:block">
          <div className="sticky top-44">
            <p className="font-display text-2xl italic">Filter by</p>
            <div className="mt-6"><Filters products={products} params={params} setParam={setParam} toggleList={toggleList} /></div>
          </div>
        </aside>

        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-plum/10 pb-5">
            <div className="flex items-center gap-3">
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <button data-testid="mobile-filters-button" className="btn-ghost !px-5 !py-2.5 lg:hidden"><SlidersHorizontal className="h-4 w-4" /> Filters{activeCount ? ` (${activeCount})` : ""}</button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[85vw] max-w-sm overflow-y-auto bg-cream" data-lenis-prevent>
                  <SheetTitle className="font-display text-2xl italic">Filter by</SheetTitle>
                  <div className="mt-6"><Filters products={products} params={params} setParam={setParam} toggleList={toggleList} /></div>
                </SheetContent>
              </Sheet>
              <p data-testid="shop-result-count" className="text-sm text-plum/75">{filtered.length} pieces</p>
              {activeCount > 0 && (
                <button data-testid="clear-filters-button" onClick={() => setParams({})} className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.15em] text-magenta"><X className="h-3.5 w-3.5" /> Clear</button>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm text-plum/75">
              Sort
              <select data-testid="shop-sort-select" value={sort} onChange={(e) => setParam("sort", e.target.value === "featured" ? null : e.target.value)} className="rounded-full border border-plum/20 bg-white px-4 py-2 text-sm text-plum focus:outline-none focus:ring-2 focus:ring-magenta">
                {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
          </div>
          {q && <p className="mt-5 text-sm text-plum/75">Results for <em className="font-display text-lg text-plum">“{params.get("q")}”</em></p>}

          {loading ? (
            <p className="py-24 text-center font-display text-2xl italic text-plum/60">Curating your edit…</p>
          ) : shown.length ? (
            <div data-testid="shop-product-grid" className="mt-8 grid grid-cols-2 gap-x-4 gap-y-10 sm:gap-x-6 md:grid-cols-3">
              {shown.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          ) : (
            <div data-testid="shop-empty-state" className="py-24 text-center">
              <p className="font-script text-5xl text-plum">Nothing here… yet</p>
              <button onClick={() => setParams({})} className="btn-luxe mt-6">Clear filters</button>
            </div>
          )}

          {pages > 1 && (
            <div className="mt-14 flex justify-center gap-2" data-testid="shop-pagination">
              {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
                <button key={n} data-testid={`shop-page-${n}`} onClick={() => { const next = new URLSearchParams(params); next.set("page", n); setParams(next); }} className={`h-11 w-11 rounded-full text-sm font-semibold transition-colors ${n === page ? "bg-plum text-cream" : "border border-plum/20 hover:bg-plum/5"}`}>
                  {n}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
